import { useEffect, useRef, useState } from "react";
import { motion, useInView, useMotionValue, useTransform, animate } from "framer-motion";
import { fetchAnalytics } from "../lib/api";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

function Counter({ value, suffix = "", duration = 2 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-20%" });
  const mv = useMotionValue(0);
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(mv, value, { duration, ease: "easeOut" });
    const unsub = mv.on("change", (v) => setDisplay(v));
    return () => { controls.stop(); unsub(); };
  }, [inView, value]);

  return (
    <span ref={ref}>
      {Math.round(display).toLocaleString()}{suffix}
    </span>
  );
}

const STATS_FALLBACK = {
  orders_delivered: 1250000,
  active_restaurants: 8400,
  cities_served: 42,
  happy_customers: 480000,
  avg_delivery_min: 28,
  five_star_ratings: 92,
};

export default function AnalyticsSection() {
  const [stats, setStats] = useState(STATS_FALLBACK);

  useEffect(() => {
    fetchAnalytics().then((d) => setStats(d)).catch(() => setStats(STATS_FALLBACK));
  }, []);

  const items = [
    { k: "Orders Delivered", v: stats.orders_delivered, s: "+" },
    { k: "Active Restaurants", v: stats.active_restaurants, s: "+" },
    { k: "Cities Served", v: stats.cities_served, s: "" },
    { k: "Happy Customers", v: stats.happy_customers, s: "+" },
    { k: "Avg Delivery (min)", v: stats.avg_delivery_min, s: "" },
    { k: "5-Star Ratings", v: stats.five_star_ratings, s: "%" },
  ];

  return (
    <section id="analytics" data-testid="analytics-section" className="relative bg-ink-900 text-white py-24 sm:py-32 overflow-hidden grain">
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-[#84CC16]/20 blur-3xl" />
        <div className="absolute -bottom-32 -right-32 w-[480px] h-[480px] rounded-full bg-[#65A30D]/15 blur-3xl" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-end justify-between gap-6 mb-14">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#A3E635] font-bold">By the Numbers</div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-3"
            >
              Food, on a <span className="text-[#84CC16]">bigger plate.</span>
            </motion.h2>
          </div>
          <Link
            to="/analytics"
            data-testid="analytics-fullpage-link"
            className="hidden md:inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-[#A3E635]"
          >
            Full report <ArrowRight size={16} />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10">
          {items.map((it, i) => (
            <div
              key={it.k}
              data-testid={`analytics-stat-${i}`}
              className="bg-ink-900 p-8 md:p-10"
            >
              <div className="font-display font-black text-4xl md:text-6xl tracking-tighter text-[#84CC16] text-glow-lime">
                <Counter value={it.v} suffix={it.s} />
              </div>
              <div className="mt-3 text-xs md:text-sm uppercase tracking-[0.25em] text-white/60">{it.k}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
